import { useState, useEffect } from "react";
import { obtenerCatalogosCacheados, hayCatalogosCompletos, descargarYCachearCatalogos } from "../api/catalogosCache";

const TIEMPO_LIMITE_PRIMERA_VEZ = 25000; // primera vez (sin nada cacheado) — vale la pena esperar más
const TIEMPO_LIMITE_REFRESCO    = 12000; // ya hay caché — solo intenta refrescar, sin exagerar la espera

/**
 * Hook que decide si la app puede mostrarse ya (porque hay catálogos
 * usables) o si hay que bloquear con la pantalla de descarga primero.
 *
 * Estados:
 *  - "verificando"  : revisando qué hay en el caché (instantáneo)
 *  - "descargando"  : NO había caché completo — bloqueando mientras se
 *                      intenta descargar, con límite de tiempo
 *  - "listo"         : hay catálogos usables (frescos o de una sesión
 *                      anterior) — la app ya puede mostrarse
 *  - "sin_datos"     : nunca se ha logrado descargar nada y no hay
 *                      caché previo — no hay forma de continuar sin
 *                      conexión al menos una vez
 */
export function useCatalogosListos() {
  const [estado, setEstado] = useState("verificando");
  const [progreso, setProgreso] = useState(null);
  const [intentos, setIntentos] = useState(0);

  useEffect(() => {
    let cancelado = false;

    (async () => {
      const yaCompleto = await hayCatalogosCompletos();

      if (yaCompleto) {
        // Ya hay algo usable — la app se muestra YA, sin esperar nada.
        // Se refresca en segundo plano por si hay novedades, sin
        // bloquear ni mostrar ninguna pantalla de carga por esto.
        if (cancelado) return;
        setEstado("listo");
        descargarYCachearCatalogos(TIEMPO_LIMITE_REFRESCO).catch(() => {});
        return;
      }

      // No hay caché completo — SÍ hay que bloquear y mostrar la
      // pantalla de descarga, con límite de tiempo.
      setEstado("descargando");
      const resultado = await descargarYCachearCatalogos(TIEMPO_LIMITE_PRIMERA_VEZ);
      if (cancelado) return;
      setProgreso(resultado);

      const quedoCompleto = await hayCatalogosCompletos();
      setEstado(quedoCompleto ? "listo" : "sin_datos");
    })();

    return () => { cancelado = true; };
  }, [intentos]);

  const reintentar = () => setIntentos(n => n + 1);

  return { estado, progreso, reintentar };
}

export function PantallaDescargandoListas() {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#eef1e7", zIndex: 3000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "36px 32px", maxWidth: 360, textAlign: "center", boxShadow: "0 10px 40px rgba(0,0,0,.12)" }}>
        <div style={{
          width: 52, height: 52, margin: "0 auto 18px", borderRadius: "50%",
          border: "4px solid #e6e9e3", borderTopColor: "#16342a",
          animation: "girar 0.8s linear infinite",
        }} />
        <style>{`@keyframes girar { to { transform: rotate(360deg); } }`}</style>
        <h5 style={{ fontWeight: 800, color: "#16342a", marginBottom: 8 }}>Preparando la aplicación</h5>
        <p style={{ fontSize: 13, color: "#6c757d", marginBottom: 0 }}>
          Descargando listas (entrevistadores, EPS, etnias...) para que funcionen sin conexión.
          Esto solo pasa una vez — puede tardar un poco si tu internet es lento.
        </p>
      </div>
    </div>
  );
}

export function PantallaSinListas({ onReintentar }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#eef1e7", zIndex: 3000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "36px 32px", maxWidth: 380, textAlign: "center", boxShadow: "0 10px 40px rgba(0,0,0,.12)" }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>📡</div>
        <h5 style={{ fontWeight: 800, color: "#b3261e", marginBottom: 8 }}>No se pudo descargar la información necesaria</h5>
        <p style={{ fontSize: 13, color: "#6c757d", marginBottom: 20 }}>
          Este dispositivo nunca ha logrado descargar las listas del formulario (entrevistadores, EPS, etc.).
          Necesitas una conexión a internet, aunque sea lenta, <strong>una sola vez</strong> — después de eso, la app funciona sin conexión con normalidad.
        </p>
        <button onClick={onReintentar} style={{
          background: "#16342a", color: "#fff", border: "none", borderRadius: 10,
          padding: "10px 24px", fontWeight: 700, fontSize: 13.5, cursor: "pointer",
        }}>
          🔄 Reintentar ahora
        </button>
      </div>
    </div>
  );
}
